import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { hrApi } from '@/api';
import { EmployeeSelect } from '@/components/selects';
import { PageActionGate } from '@/components/auth/PageActionGate';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import { FormGrid } from '@/components/ui/FormGrid';
import { FormActions } from '@/components/ui/FormActions';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { ViewModeToggle, type ViewMode } from '@/components/ui/ViewModeToggle';
import { HrCalendarGranularityToggle } from '@/modules/hr/components/HrCalendarGranularityToggle';
import {
  HrEmployeeScheduleGrid,
  hrEmployeeInitials,
  type HrScheduleEmployee,
} from '@/modules/hr/components/HrEmployeeScheduleGrid';
import {
  HR_ATTENDANCE_STYLES,
  hrAttendanceDayKey,
  hrAttendanceDayTooltip,
  hrAttendanceRowMinutes,
  hrDateKey,
  hrCalendarDaysForGranularity,
  hrCalendarRangeForGranularity,
  hrFormatMinutes,
  hrPrimaryAttendanceStatus,
  hrSumAttendanceMinutes,
  type HrCalendarGranularity,
} from '@/modules/hr/components/hrCalendarUtils';
import { HrWorkforceFilterBar, type HrWorkforceFilters } from '@/modules/hr/components/HrWorkforceFilterBar';
import { dataTablePageProps, usePagination } from '@/hooks/usePagination';
import { useEmployeeOptions } from '@/hooks/useEmployeeOptions';
import type { HrAttendance } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { cn } from '@/utils/cn';
import { fetchAllHrPages } from '@/modules/hr/components/hrCalendarFetch';
import { HrScheduleCalendarLegend } from '@/modules/hr/components/HrScheduleCalendarLegend';
import { useEmployeeScheduleCalendar } from '@/modules/hr/hooks/useEmployeeScheduleCalendar';
import { useAppSelector } from '@/hooks/useAppSelector';

const ATTENDANCE_STATUS_OPTIONS = [
  { value: 'PRESENT', label: 'Present' },
  { value: 'ABSENT', label: 'Absent' },
  { value: 'LATE', label: 'Late' },
  { value: 'HALF_DAY', label: 'Half day' },
  { value: 'WFH', label: 'Work from home' },
  { value: 'HOLIDAY', label: 'Holiday' },
  { value: 'WEEKEND', label: 'Weekend' },
];

export function HrAttendancePage() {
  const pagination = usePagination();
  const currentUserId = useAppSelector((s) => s.auth.user?.userId);
  const [searchParams] = useSearchParams();
  const initialEmployeeId = searchParams.get('employeeId') ?? '';
  const { employees, getLabel } = useEmployeeOptions({ pageSize: 200, hrScoped: true });
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [granularity, setGranularity] = useState<HrCalendarGranularity>('month');
  const [viewDate, setViewDate] = useState(() => new Date());
  const [filters, setFilters] = useState<HrWorkforceFilters>({
    employeeId: initialEmployeeId,
    departmentId: '',
    status: '',
    dateFrom: '',
    dateTo: '',
  });

  const [data, setData] = useState<HrAttendance[]>([]);
  const [calendarData, setCalendarData] = useState<HrAttendance[]>([]);
  const [loading, setLoading] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);

  const [employeeId, setEmployeeId] = useState(initialEmployeeId);
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [checkInMode, setCheckInMode] = useState('MANUAL');
  const [submitting, setSubmitting] = useState(false);

  const [cellModal, setCellModal] = useState<{
    employeeId: number;
    dayKey: string;
    rows: HrAttendance[];
  } | null>(null);
  const [modalCheckIn, setModalCheckIn] = useState('');
  const [modalCheckOut, setModalCheckOut] = useState('');
  const [modalCheckInMode, setModalCheckInMode] = useState('MANUAL');

  const listParams = useMemo(() => {
    const range =
      filters.dateFrom && filters.dateTo
        ? { dateFrom: filters.dateFrom, dateTo: filters.dateTo }
        : viewMode === 'calendar'
          ? hrCalendarRangeForGranularity(viewDate, granularity)
          : {};
    return {
      page: pagination.page,
      size: pagination.size,
      employeeId: filters.employeeId ? Number(filters.employeeId) : undefined,
      departmentId: filters.departmentId ? Number(filters.departmentId) : undefined,
      requestStatus: filters.status || undefined,
      ...range,
    };
  }, [filters, pagination.page, pagination.size, viewDate, granularity, viewMode]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await hrApi.listAttendance(listParams);
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load attendance');
    } finally {
      setLoading(false);
    }
  }, [listParams]);

  const loadCalendar = useCallback(async () => {
    setCalendarLoading(true);
    try {
      const { start, end } =
        filters.dateFrom && filters.dateTo
          ? { start: filters.dateFrom, end: filters.dateTo }
          : hrCalendarRangeForGranularity(viewDate, granularity);
      const rows = await fetchAllHrPages((page) =>
        hrApi.listAttendance({
          page,
          size: 200,
          dateFrom: start,
          dateTo: end,
          employeeId: filters.employeeId ? Number(filters.employeeId) : undefined,
          departmentId: filters.departmentId ? Number(filters.departmentId) : undefined,
          requestStatus: filters.status || undefined,
        }),
      );
      setCalendarData(rows);
    } catch (err) {
      showApiError(err, 'Failed to load attendance calendar');
    } finally {
      setCalendarLoading(false);
    }
  }, [viewDate, granularity, filters]);

  useEffect(() => {
    if (viewMode === 'list') {
      load();
    } else {
      loadCalendar();
    }
  }, [viewMode, load, loadCalendar]);

  useEffect(() => {
    if (!cellModal) return;
    setModalCheckIn(`${cellModal.dayKey}T09:00`);
    setModalCheckOut(`${cellModal.dayKey}T17:00`);
    setModalCheckInMode('MANUAL');
  }, [cellModal]);

  const handleCheckInChange = (next: string) => {
    setCheckIn(next);
    if (checkOut && next && checkOut <= next) setCheckOut('');
  };

  const handleModalCheckInChange = (next: string) => {
    setModalCheckIn(next);
    if (modalCheckOut && next && modalCheckOut <= next) setModalCheckOut('');
  };

  const refreshAll = () => {
    load();
    if (viewMode === 'calendar') loadCalendar();
  };

  const markAttendance = async () => {
    const empId = Number(employeeId);
    if (!empId) {
      notify.error('Select an employee');
      return;
    }
    if (checkIn && checkOut && checkOut <= checkIn) {
      notify.error('Check-out must be after check-in');
      return;
    }
    setSubmitting(true);
    try {
      await hrApi.markAttendance({
        employeeId: empId,
        checkIn: checkIn,
        checkOut: checkOut || undefined,
        checkInMode,
      });
      notify.success('Attendance recorded');
      setEmployeeId('');
      setCheckIn('');
      setCheckOut('');
      refreshAll();
    } catch (err) {
      showApiError(err, 'Failed to mark attendance');
    } finally {
      setSubmitting(false);
    }
  };

  const markAttendanceFromCalendar = async () => {
    if (!cellModal) return;
    if (!modalCheckIn) {
      notify.error('Check-in time is required');
      return;
    }
    if (modalCheckOut && modalCheckOut <= modalCheckIn) {
      notify.error('Check-out must be after check-in');
      return;
    }
    setSubmitting(true);
    try {
      await hrApi.markAttendance({
        employeeId: cellModal.employeeId,
        checkIn: modalCheckIn,
        checkOut: modalCheckOut || undefined,
        checkInMode: modalCheckInMode,
      });
      notify.success('Attendance recorded');
      setCellModal(null);
      refreshAll();
    } catch (err) {
      showApiError(err, 'Failed to mark attendance');
    } finally {
      setSubmitting(false);
    }
  };

  const approveAttendance = async (id: number) => {
    const approver = currentUserId ?? 0;
    if (!approver) {
      notify.error('Unable to resolve approver');
      return;
    }
    try {
      await hrApi.approveAttendance(id, approver);
      notify.success('Attendance approved');
      setCellModal(null);
      refreshAll();
    } catch (err) {
      showApiError(err, 'Approval failed');
    }
  };

  const calendarDays = useMemo(
    () => hrCalendarDaysForGranularity(viewDate, granularity),
    [viewDate, granularity],
  );
  const { start: rangeStart, end: rangeEnd } = hrCalendarRangeForGranularity(viewDate, granularity);

  const attendanceByEmployeeDay = useMemo(() => {
    const map = new Map<number, Map<string, HrAttendance[]>>();
    for (const row of calendarData) {
      const key = hrAttendanceDayKey(row.checkIn);
      if (!key || key < rangeStart || key > rangeEnd) continue;
      const byDay = map.get(row.employeeId) ?? new Map<string, HrAttendance[]>();
      const list = byDay.get(key) ?? [];
      list.push(row);
      byDay.set(key, list);
      map.set(row.employeeId, byDay);
    }
    return map;
  }, [calendarData, rangeStart, rangeEnd]);

  const scheduleEmployees = useMemo((): HrScheduleEmployee[] => {
    let source = employees;
    if (filters.employeeId) {
      source = employees.filter((e) => String(e.id) === filters.employeeId);
    }
    return [...source]
      .sort((a, b) => {
        const an = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim();
        const bn = `${b.firstName ?? ''} ${b.lastName ?? ''}`.trim();
        return an.localeCompare(bn);
      })
      .map((employee) => ({
        id: employee.id,
        name: [employee.firstName, employee.lastName].filter(Boolean).join(' ') || getLabel(employee.id, true),
        subtitle: employee.employeeCode ?? undefined,
        initials: hrEmployeeInitials(employee.firstName, employee.lastName),
      }));
  }, [employees, filters.employeeId, getLabel]);

  const scheduleEmployeeIds = useMemo(() => scheduleEmployees.map((e) => e.id), [scheduleEmployees]);
  const scheduleCalendar = useEmployeeScheduleCalendar({
    viewDate,
    employeeIds: scheduleEmployeeIds,
    enabled: viewMode === 'calendar' && scheduleEmployeeIds.length > 0,
  });

  const employeeMonthHours = useMemo(() => {
    const totals = new Map<number, number>();
    for (const row of calendarData) {
      const key = hrAttendanceDayKey(row.checkIn);
      if (!key || key < rangeStart || key > rangeEnd) continue;
      totals.set(row.employeeId, (totals.get(row.employeeId) ?? 0) + hrAttendanceRowMinutes(row) / 60);
    }
    return totals;
  }, [calendarData, rangeEnd, rangeStart]);

  const columns: Column<HrAttendance>[] = [
    { key: 'id', header: '#' },
    { key: 'employeeId', header: 'Employee', render: (row) => getLabel(row.employeeId, true) },
    { key: 'checkIn', header: 'Check in' },
    { key: 'checkOut', header: 'Check out' },
    { key: 'attendanceStatus', header: 'Status' },
    { key: 'checkInMode', header: 'Mode' },
    {
      key: 'workedMinutes',
      header: 'Hours',
      render: (row) => hrFormatMinutes(row.workedMinutes),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Attendance"
        description="Hierarchy-scoped attendance with calendar actions and filters"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'Attendance' }]}
      />

      <HrWorkforceFilterBar
        filters={filters}
        onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
        mode="attendance"
        statusOptions={ATTENDANCE_STATUS_OPTIONS}
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <ViewModeToggle value={viewMode} onChange={setViewMode} />
        {viewMode === 'calendar' && (
          <HrCalendarGranularityToggle value={granularity} onChange={setGranularity} />
        )}
      </div>

      {viewMode === 'list' && (
        <div className="mb-6 rounded-lg border border-slate-200 p-4 dark:border-slate-700">
          <h3 className="mb-2 text-sm font-medium">Mark attendance</h3>
          <FormGrid columns={4} className="items-end">
            <EmployeeSelect value={employeeId} onChange={setEmployeeId} hrScoped />
            <DateTimePicker label="Check in" value={checkIn} onChange={handleCheckInChange} />
            <DateTimePicker
              label="Check out"
              value={checkOut}
              onChange={setCheckOut}
              min={checkIn || undefined}
              disabled={!checkIn}
            />
            <Select
              label="Mode"
              value={checkInMode}
              onChange={(v) => setCheckInMode(v)}
              options={[
                { value: 'MANUAL', label: 'MANUAL' },
                { value: 'WEB', label: 'WEB' },
                { value: 'MOBILE', label: 'MOBILE' },
                { value: 'QR', label: 'QR' },
              ]}
            />
          </FormGrid>
          <FormActions
            primaryLabel="Record"
            primaryLoading={submitting}
            onPrimary={() => void markAttendance()}
            onReset={() => {
              setEmployeeId('');
              setCheckIn('');
              setCheckOut('');
              setCheckInMode('MANUAL');
            }}
          />
        </div>
      )}

      {viewMode === 'calendar' && (
        <p className="mb-4 text-sm text-muted">
          Each day shows total hours worked. Hover for check-in / check-out details · click to view or add punches.
        </p>
      )}

      {viewMode === 'list' ? (
        <DataTable columns={columns} data={data} loading={loading} keyField="id" {...dataTablePageProps(pagination)} />
      ) : (
        <div className="card p-4">
          <HrEmployeeScheduleGrid
            viewDate={viewDate}
            onViewDateChange={setViewDate}
            days={calendarDays}
            employees={scheduleEmployees}
            loading={calendarLoading || scheduleCalendar.loading}
            getEmployeeTotal={(empId) => employeeMonthHours.get(empId) ?? null}
            getCellBackgroundClass={scheduleCalendar.getCellBackgroundClass}
            getHeaderBackgroundClass={scheduleCalendar.getHeaderBackgroundClass}
            getCellTitle={(empId, day, dayKey) => {
              const scheduleTip = scheduleCalendar.getDayTooltip(empId, day);
              const items = attendanceByEmployeeDay.get(empId)?.get(dayKey) ?? [];
              const attendanceTip = items.length > 0 ? hrAttendanceDayTooltip(items) : undefined;
              if (scheduleTip && attendanceTip) return `${scheduleTip}\n${attendanceTip}`;
              return attendanceTip ?? scheduleTip;
            }}
            calendarLegend={<HrScheduleCalendarLegend />}
            emptyMessage="No employees in your visibility scope — adjust filters or hierarchy."
            onCellClick={(empId, day) => {
              const dayKey = hrDateKey(day);
              const items = attendanceByEmployeeDay.get(empId)?.get(dayKey) ?? [];
              setCellModal({ employeeId: empId, dayKey, rows: items });
            }}
            renderCell={(empId, _day, dayKey) => {
              const items = attendanceByEmployeeDay.get(empId)?.get(dayKey) ?? [];
              if (items.length === 0) return null;
              const totalMinutes = hrSumAttendanceMinutes(items);
              const status = hrPrimaryAttendanceStatus(items);
              return (
                <div
                  className={cn(
                    'rounded border px-1 py-0.5 text-center text-[10px] font-medium leading-tight',
                    HR_ATTENDANCE_STYLES[status] ?? HR_ATTENDANCE_STYLES.PRESENT,
                  )}
                >
                  <span className="block">{hrFormatMinutes(totalMinutes)}</span>
                  {items.length > 1 ? (
                    <span className="block text-[9px] font-normal opacity-80">{items.length} punches</span>
                  ) : (
                    <span className="block text-[9px] font-normal opacity-80">{status.replace('_', ' ')}</span>
                  )}
                </div>
              );
            }}
          />
        </div>
      )}

      <Modal
        open={cellModal != null}
        onClose={() => setCellModal(null)}
        title={cellModal ? `Attendance · ${getLabel(cellModal.employeeId, true)} · ${cellModal.dayKey}` : ''}
      >
        {cellModal && (
          <div className="space-y-4">
            {cellModal.rows.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-sm font-medium text-slate-700 dark:text-slate-200">Recorded</h4>
                  <span className="text-sm text-slate-500">
                    Day total: {hrFormatMinutes(hrSumAttendanceMinutes(cellModal.rows))}
                  </span>
                </div>
                {cellModal.rows.map((row) => (
                  <div key={row.id} className="rounded border border-slate-200 p-3 text-sm dark:border-slate-700">
                    <div>Status: <strong>{row.attendanceStatus}</strong></div>
                    <div>Check in: {row.checkIn ?? '—'}</div>
                    <div>Check out: {row.checkOut ?? '—'}</div>
                    <div>Worked: {hrFormatMinutes(hrAttendanceRowMinutes(row))}</div>
                    <PageActionGate action="edit">
                      <Button
                        size="sm"
                        className="mt-2"
                        variant="secondary"
                        onClick={() => void approveAttendance(row.id)}
                      >
                        Approve
                      </Button>
                    </PageActionGate>
                  </div>
                ))}
              </div>
            )}
            <PageActionGate action="create">
              <div className="space-y-3 rounded-lg border border-dashed border-slate-300 p-3 dark:border-slate-600">
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  {cellModal.rows.length === 0 ? 'Record attendance' : 'Add attendance'}
                </h4>
                <FormGrid columns={2} className="items-end">
                  <DateTimePicker label="Check in" value={modalCheckIn} onChange={handleModalCheckInChange} />
                  <DateTimePicker
                    label="Check out"
                    value={modalCheckOut}
                    onChange={setModalCheckOut}
                    min={modalCheckIn || undefined}
                    disabled={!modalCheckIn}
                  />
                  <Select
                    label="Mode"
                    value={modalCheckInMode}
                    onChange={setModalCheckInMode}
                    options={[
                      { value: 'MANUAL', label: 'MANUAL' },
                      { value: 'WEB', label: 'WEB' },
                      { value: 'MOBILE', label: 'MOBILE' },
                      { value: 'QR', label: 'QR' },
                    ]}
                  />
                  <Button loading={submitting} onClick={() => void markAttendanceFromCalendar()}>
                    Record attendance
                  </Button>
                </FormGrid>
              </div>
            </PageActionGate>
          </div>
        )}
      </Modal>
    </div>
  );
}
